import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_BASE, getToken, formatError } from "@/lib/api";
import { StatusPill } from "@/components/StatusBanner";
import { WidgetError } from "@/components/WidgetError";
import { ProtectedImage } from "@/components/ProtectedImage";
import { openProtectedFile, downloadProtectedFile } from "@/lib/protectedFile";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Eye, Download } from "lucide-react";

export default function CustomerOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get(`/portal/orders/${id}`)
       .then(({ data }) => { if (!cancelled) setOrder(data); })
       .catch((err) => { if (!cancelled) setError(err); });
    return () => { cancelled = true; };
  }, [id, reloadKey]);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  const openPdf = async () => {
    try {
      const res = await fetch(`${API_BASE}/portal/orders/${id}/receipt.pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Failed (${res.status})`);
      }
      const b = await res.blob();
      window.open(URL.createObjectURL(b), "_blank");
    } catch (e) {
      toast.error(formatError(e, "Could not open receipt PDF"));
    }
  };

  if (error) {
    return (
      <div className="max-w-3xl">
        <WidgetError
          error={error}
          onRetry={retry}
          title="Could not load this order"
          testId="portal-order-error"
        />
      </div>
    );
  }
  if (!order) return <div className="label-overline text-slate-500">Loading…</div>;

  return (
    <div className="max-w-3xl">
      <div className="label-overline text-slate-500 flex items-center gap-2">
        <Link to="/portal/history">History</Link><span>/</span>
        <span className="font-mono">{order.order_no}</span>
      </div>
      <div className="flex items-center gap-3 mt-2 mb-6">
        <h1 className="text-3xl font-black tracking-tight">Order {order.order_no}</h1>
        <StatusPill status={order.status} />
      </div>

      <div className="bg-white border border-slate-200 p-5">
        <div className="grid grid-cols-2 gap-y-3 text-sm">
          <div className="label-overline">Registration</div>
          <div className="font-mono font-bold text-lg text-blue-700">{order.registration_no}</div>
          <div className="label-overline">Service</div>
          <div>{order.service_type_snapshot?.label}</div>
          <div className="label-overline">Opened</div>
          <div className="font-mono text-xs">{fmtDate(order.opened_at)}</div>
          {order.completed_at && (<>
            <div className="label-overline">Completed</div>
            <div className="font-mono text-xs">{fmtDate(order.completed_at)}</div>
          </>)}
          {order.notes && (<>
            <div className="label-overline">Notes</div>
            <div className="whitespace-pre-wrap">{order.notes}</div>
          </>)}
        </div>

        <div className="mt-5 pt-4 border-t border-slate-200 flex gap-2">
          <a href={`${API_BASE}/portal/orders/${id}/receipt.html`}
             target="_blank" rel="noreferrer"
             className="border border-slate-300 rounded py-2 px-4 hover:bg-slate-50 text-sm">
            View HTML receipt
          </a>
          <Button className="rounded bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="portal-order-pdf" onClick={openPdf}>Download PDF</Button>
        </div>
      </div>

      {order.receipt_photo_file_id && (
        <div className="mt-4 bg-white border border-slate-200 p-5"
             data-testid="portal-signed-receipt-section">
          <div className="label-overline mb-2">Signed Receipt Photo</div>
          <p className="text-xs text-slate-600 mb-3">
            Proof photo of the paper receipt signed by your driver on
            completion of this order.
          </p>
          <div className="border border-slate-200 rounded overflow-hidden mb-3">
            <ProtectedImage
              path={`/portal/files/${order.receipt_photo_file_id}`}
              alt="Signed receipt"
              testId="portal-receipt-photo-img"
              className="w-full h-auto max-h-96 object-contain bg-slate-50" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" data-testid="portal-receipt-photo-view"
                    onClick={() => openProtectedFile(
                      `/portal/files/${order.receipt_photo_file_id}`,
                      "Could not open receipt")}
                    className="rounded flex items-center justify-center gap-2">
              <Eye size={14}/> View signed receipt
            </Button>
            <Button variant="outline" data-testid="portal-receipt-photo-download"
                    onClick={() => downloadProtectedFile(
                      `/portal/files/${order.receipt_photo_file_id}`,
                      `signed-receipt-${order.order_no}.jpg`,
                      "Could not download receipt")}
                    className="rounded flex items-center justify-center gap-2">
              <Download size={14}/> Download signed receipt
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
